import {
  Vector3,
  Raycaster,
  Vector2,
  PlaneGeometry,
  MeshBasicMaterial,
  DoubleSide,
  Euler,
  AxesHelper,
  Matrix4,
  Frustum,
  TextureLoader,
  MathUtils,
  Mesh,
  Clock,
  Object3D,
  AnimationMixer,
  RingGeometry,
} from "three";
import { EJPlayerPipelineModuleResult } from "./8thwall/ej-player-pipeline-module";
import { artworkData, ArtworkData } from "./artworks";
import { EJPlayerBase, EJPlayerContent, ContentModule } from "./libs/ejx.es";
import { EJCubeProxy } from "./ejx/EJCubeProxy.js";
import { EJAppImmersiveARControls } from "./ejx/EJAppImmersiveARControls.js";
import { TaggedFsm } from "@/utils/tagged-fsm.js";
import { RendererEvents } from "./index.js";
import { Emitter } from "mitt";
import FloorSymbol from "../assets/floor_symbol.png";
import { createContentModule } from "./createContentModule.js";
import { InitEJXCubeResult } from ".";
import { ArtworkState } from "@/context/ArtworkContext.js";
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { setAnimations, startAnimation } from "./rubixContent.js";
import { UIContentState } from "./image-targets.js";
import { ContentState, contentStateManager } from "./contentStateManager.js";

const params = new URLSearchParams(window.location.search);
const noAR = params.get("noAR") === "1";

const VEC2_NDC_CENTRE = new Vector2();

// eslint-disable-next-line @typescript-eslint/no-namespace
namespace S {
  export type None = { s: "None" };
  export type LoadError = { s: "LoadError"; error: Error };
  export type Loading = {
    s: "Loading";
    content: EJPlayerContent;
    loadProgress: number;
    artwork: ArtworkData;
    arControls: EJAppImmersiveARControls;
  };
  export type Positioning = {
    s: "Positioning";
    artwork: ArtworkData;
  };
  export type Viewing = {
    s: "Viewing";
    content: EJPlayerContent;
    artwork: ArtworkData;
    arControls: EJAppImmersiveARControls;
  };
  export type NoneOrLoadError = None | LoadError;
  export type All = NoneOrLoadError | Loading | Positioning | Viewing;
}

// eslint-disable-next-line @typescript-eslint/no-namespace
namespace E {
  // Pointer events
  export type PointerDown = { e: "PointerDown"; args: [event: PointerEvent] };
  export type PointerMove = { e: "PointerMove"; args: [event: PointerEvent] };
  export type PointerUp = { e: "PointerUp"; args: [event: PointerEvent] };

  // Artwork events
  export type StartRepositioning = {
    e: "StartRepositioning";
    args: [artwork: ArtworkData, contentModule: ContentModule];
  };
  export type UpdatePositioning = {
    e: "UpdatePositioning";
    args: [position: Vector3];
  };
  export type FinishRepositioning = {
    e: "FinishRepositioning";
    args: [position: Vector3];
  };
  export type StartLoad = {
    e: "StartLoad";
    args: [artwork: ArtworkData, contentModule: ContentModule];
  };
  export type LoadComplete = { e: "LoadComplete"; args: [] };
  export type ClearArtwork = { e: "ClearArtwork"; args: [] };
  export type RepositionCurrent = { e: "RepositionCurrent"; args: [] };
  export type StartViewingContent = { e: "StartViewingContent"; args: [] };
  export type UpdateContentView = { e: "UpdateContentView"; args: [] };
  export type EnterContent = { e: "EnterContent"; args: [number] };
  export type ExitContent = { e: "ExitContent"; args: [] };

  // Aggregated
  export type All =
    | PointerDown
    | PointerMove
    | PointerUp
    | StartLoad
    | LoadComplete
    | StartRepositioning
    | UpdatePositioning
    | FinishRepositioning
    | ClearArtwork
    | RepositionCurrent
    | StartViewingContent
    | UpdateContentView
    | EnterContent
    | ExitContent;
}

export function init3dExperience(
  module: EJPlayerPipelineModuleResult,
  player: EJPlayerBase | undefined,
  emitter: Emitter<RendererEvents>,
  contentContainer: Object3D,
  models: GLTF[],
  setEJXCube: (artwork: ArtworkData) => Promise<void>
) {
  if (!player) {
    console.warn("Player not found");
    return;
  }

  let hasBeenRepositioned = false;
  let trackingStatus: string | undefined;
  let pauseRenderer = false;
  let oldProgress = 0;

  const clock = new Clock(true);
  const raycaster = new Raycaster();

  const tempVec3 = new Vector3();
  const tempEuler = new Euler();
  const objectWorldPosition = new Vector3();
  const cameraInverseMatrix = new Matrix4();
  const frustum = new Frustum();

  // const reticleTexture = new TextureLoader().load(FloorSymbol);
  const reticle = new Mesh(
    // new PlaneGeometry(0.75, 0.75).rotateX(-Math.PI / 2),
    new RingGeometry(0.15, 0.3, 32).rotateX(-Math.PI / 2),
    new MeshBasicMaterial({
      color: 0xffffff,
      // map: reticleTexture,
      transparent: true,
      side: DoubleSide,
      depthTest: false,
      depthWrite: false,
    })
  );
  reticle.name = "Reticle";
  reticle.visible = false;
  player.scene.add(reticle);

  // Floor plane (DEV view)
  const floorPlane = new Mesh(
    new PlaneGeometry(20, 20, 1, 1),
    new MeshBasicMaterial({
      color: 0xffff00,
      wireframe: true,
      transparent: true,
      opacity: 0.5,
      depthTest: false,
      depthWrite: false,
    })
  );
  floorPlane.name = "FloorPlane";
  floorPlane.visible = import.meta.env.DEV;
  floorPlane.rotateX(-Math.PI / 2);
  floorPlane.position.set(0, 0, 0);
  player.scene.add(floorPlane);

  // Camera & helpers
  player.scene.add(player.camera);
  player.camera.position.set(0, 1.5, 0);

  const hitMarker = new AxesHelper();
  hitMarker.visible = import.meta.env.DEV; // Only show in dev
  player.scene.add(hitMarker);

  if (module && module.emitter) {
    module.emitter.on("trackingStatus", (ev) => {
      if (emitter && emitter.emit) emitter.emit("tracking-status", ev);
      trackingStatus = ev;
    });
  }

  const canvasEl = document.getElementById("xr-canvas") as HTMLCanvasElement;
  let activePointers = 0;

  const handleRawPointerMove = (event: PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (fsm.can("PointerMove")) fsm.dispatch("PointerMove", event);
  };

  const handleRawPointerUp = (event: PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (fsm.can("PointerUp")) fsm.dispatch("PointerUp", event);
    activePointers -= 1;

    if (activePointers === 0) {
      document.body.removeEventListener("pointermove", handleRawPointerMove);
      document.body.removeEventListener("pointerup", handleRawPointerUp);
    }
  };

  canvasEl.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (fsm.can("PointerDown")) fsm.dispatch("PointerDown", event);

    if (activePointers === 0) {
      document.body.addEventListener("pointermove", handleRawPointerMove);
      document.body.addEventListener("pointerup", handleRawPointerUp);
    }
    activePointers += 1;
  });

  document.addEventListener("touchstart", () => {
    if (fsm.value.s === "Positioning" && (noAR || reticle.visible)) {
      fsm.dispatch("FinishRepositioning", reticle.position);
    }
  });

  contentStateManager.init(contentContainer);

  let lastContentState = contentStateManager.getState();

  let contentId = 0;
  let transitionTriggered = false;
  let contentIsLoading = false;

  const mixers: AnimationMixer[] = [];
  const animContentIn = setAnimations(
    contentContainer,
    models[0],
    "animContentIn"
  );
  const animContentOut = setAnimations(
    contentContainer,
    models[1],
    "animContentOut"
  );
  mixers.push(animContentIn.mixer, animContentOut.mixer);
  animContentIn.mixer.addEventListener("finished", () => {
    contentStateManager.setState(ContentState.INTRO_ANIM);
    emitter.emit("set-ui-state", UIContentState.MAIN_MENU);
  });

  animContentOut.mixer.addEventListener("finished", () => {
    if (
      contentStateManager.getState() === ContentState.CONTENT_IN_TRANSITION &&
      fsm.can("StartViewingContent")
    ) {
      fsm.dispatch("StartViewingContent");
    }
    emitter.emit("set-ui-state", UIContentState.STORY_CONTENT);
  });

  const cubeProxy = new EJCubeProxy(new Object3D());
  cubeProxy.visible = true;
  const fsm = new TaggedFsm<S.All, E.All>({ s: "None" })

    .defineTransition(
      "None",
      "StartRepositioning",
      (_prev, artwork, contentModule) => {
        if (emitter?.emit) emitter.emit("content-repositioning");
        hasBeenRepositioned = false;
        contentStateManager.setState(ContentState.HIDDEN);

        return { s: "Positioning", artwork, contentModule };
      }
    )

    .defineTransition("Positioning", "UpdatePositioning", (prev, position) => {
      hitMarker.position.set(position.x, 0.0, position.z);
      reticle.position.lerp(new Vector3(position.x, -0.5, position.z), 0.25);
      noAR
        ? contentContainer.position.set(0.0, 0.0, 0.0)
        : contentContainer.position.set(position.x, 1.0, position.z);
      cubeProxy.container.position.set(position.x, 1.0, position.z);

      tempVec3
        .copy(player.camera.position)
        .sub(contentContainer.position)
        .normalize();
      const rotationY = Math.atan2(tempVec3.x, tempVec3.z);
      tempEuler.set(0, rotationY, 0, "XYZ");
      contentContainer.quaternion.setFromEuler(tempEuler);
      reticle.quaternion.setFromEuler(tempEuler);

      return prev;
    })

    .defineTransition(
      "Positioning",
      "FinishRepositioning",
      (prev, position) => {
        if (emitter?.emit) emitter.emit("content-placed");
        module?.emitter.emit("artwork-state", ArtworkState.VIEWING);
        player.scene.remove(cubeProxy.container);
        reticle.visible = false;
        noAR
          ? contentContainer.position.set(0.0, 0.0, 0.0)
          : contentContainer.position.set(position.x, 1.0, position.z);

        if (!hasBeenRepositioned) {
          contentStateManager.setState(ContentState.INTRO_ANIM);

          startAnimation(animContentIn.actions);
        }
        const shared = {
          player,
          cubeProxy,
          cubeFloorPos: reticle.position,
        };
        const arControls = new EJAppImmersiveARControls(0, shared);
        if (emitter) {
          arControls.emitter.on("*", (type: keyof RendererEvents) =>
            emitter.emit(type)
          );
        }

        arControls.enableDragRotX = true;
        arControls.enableDragRotY = false;
        arControls.enablePinchPan = true;
        arControls.enablePinchScale = true;

        return {
          ...prev,
          s: "Viewing",
          loadProgress: 0,
          arControls,
        };
      }
    )

    .defineTransition(
      "Loading",
      "LoadComplete",
      ({ content, artwork, arControls }) => {
        if (emitter?.emit) emitter.emit("content-loaded");
        return {
          s: "Viewing",
          content,
          artwork,
          arControls,
        };
      }
    )

    .defineTransition(
      "Viewing",
      "RepositionCurrent",
      ({ content, artwork }) => {
        if (emitter?.emit) emitter.emit("content-repositioning");
        hasBeenRepositioned = true;
        return {
          s: "Positioning",
          content,
          artwork,
        };
      }
    )

    .defineTransition("Viewing", "StartViewingContent", (prev) => {
      contentStateManager.setState(ContentState.PORTAL);

      return { ...prev, s: "Viewing" };
    })
    .defineTransition("Viewing", "EnterContent", (prev, id) => {
      contentStateManager.setState(ContentState.OUTRO_ANIM);
      startAnimation(animContentOut.actions);
      emitter.emit("set-ui-state", UIContentState.NONE);
      return { ...prev, s: "Viewing" };
    })
    .defineTransition("Viewing", "ExitContent", (prev) => {
      emitter.emit("set-ui-state", UIContentState.NONE);
      startAnimation(animContentIn.actions);
      emitter.emit("start-transition", {
        contentState: ContentState.CONTENT_OUT_TRANSITION,
        animationState: "out",
      });
      return { ...prev, s: "Viewing" };
    })

    .defineTransition(["Loading", "Viewing"], "PointerDown", (state, event) => {
      state.arControls.pointerDownHandler(event);
      return state;
    })
    .defineTransition(["Loading", "Viewing"], "PointerMove", (state, event) => {
      state.arControls.pointerMoveHandler(event);
      return state;
    })
    .defineTransition(["Loading", "Viewing"], "PointerUp", (state, event) => {
      state.arControls.pointerUpHandler(event);
      return state;
    })

    .defineTransition(["Loading", "Viewing"], "ClearArtwork", ({ content }) => {
      if (content) {
        player.contentKillAll();
      }
      oldProgress = 0;
      player.contentKillAll();
      return { s: "None" };
    });

  emitter.on(
    "start-transition",
    ({
      contentState,
      animationState,
    }: {
      contentState: ContentState;
      animationState: "in" | "out";
    }) => {
      contentStateManager.setState(contentState);
    }
  );

  module?.emitter?.on("animate-content-out", () => {
    if (fsm.can("ExitContent")) {
      fsm.dispatch("ExitContent");
    }
  });
  module?.emitter?.on("content-select", async (id: number) => {
    contentId = id;
    contentIsLoading = true;
    emitter.emit("set-ui-state", UIContentState.NONE);
    switch (id) {
      case 0:
        await setEJXCube(artworkData[0]);
        break;
      case 1:
        await setEJXCube(artworkData[1]);
        break;
      case 2:
        await setEJXCube(artworkData[2]);
        break;
      default:
        break;
    }
  });

  /**
   * -------------------------------
   * Render & Resize Overrides
   * -------------------------------
   */
  player.resizeOverrideFunc = () => {
    resize();
  };

  player.renderOverrideFunc = () => {
    if (pauseRenderer) {
      return;
    }

    const deltaTime = clock.getDelta();

    const currentState = contentStateManager.getState();
    if (currentState !== lastContentState) {
      transitionTriggered = false;
      lastContentState = currentState;
    }

    if (contentIsLoading) {
      const newProgress = player.contentLoadProgress();
      if (player.contentLoaded()) {
        if (emitter.emit) {
          emitter.emit("content-load-progress", 1);
          emitter.emit("content-loaded");
        }
        if (fsm.can("EnterContent")) {
          fsm.dispatch("EnterContent", contentId);
        }
        contentIsLoading = false;
      } else if (oldProgress !== newProgress) {
        oldProgress = newProgress;
        if (emitter.emit) emitter.emit("content-load-progress", newProgress);
      }
    }

    if (mixers) mixers.forEach((mixer) => mixer.update(deltaTime));

    if (!transitionTriggered) {
      if (currentState === ContentState.OUTRO_ANIM) {
        if (
          animContentOut.actions.values().next().value!.time >= 1.75 &&
          !transitionTriggered
        ) {
          if (emitter.emit)
            emitter.emit("start-transition", {
              contentState: ContentState.CONTENT_IN_TRANSITION,
              animationState: "in",
            });

          transitionTriggered = true;
        }
      }
    }

    let current = fsm.value;

    if (current.s === "Positioning") {
      floorPlane.position.copy(player.camera.position);
      floorPlane.position.y = 0;

      raycaster.setFromCamera(VEC2_NDC_CENTRE, player.camera);
      const hits = raycaster.intersectObject(floorPlane);
      const [first] = hits;
      if (first) {
        current = fsm.dispatch("UpdatePositioning", first.point);
      }
    }

    if (current.s === "Viewing") {
      contentContainer.getWorldPosition(objectWorldPosition);
      cameraInverseMatrix.copy(player.camera.matrixWorld).invert();
      cameraInverseMatrix.multiplyMatrices(
        player.camera.projectionMatrix,
        player.camera.matrixWorldInverse
      );
      frustum.setFromProjectionMatrix(cameraInverseMatrix);

      const objectPosRelCam =
        objectWorldPosition.applyMatrix4(cameraInverseMatrix);
      const isLeftOfCamera = objectPosRelCam.x < 0;
      const isOnScreen = frustum.containsPoint(contentContainer.position);

      if (!isOnScreen && isLeftOfCamera) {
        emitter?.emit("on-artwork-helper-change", "left");
      } else if (!isOnScreen && !isLeftOfCamera) {
        emitter?.emit("on-artwork-helper-change", "right");
      } else {
        emitter?.emit("on-artwork-helper-change", "none");
      }
    }

    const elapsedTime = clock.elapsedTime;

    // if (current.s === "Loading") {
    //   cubeProxy.timeElapsed = elapsedTime;
    //   cubeProxy.opacity = MathUtils.lerp(
    //     cubeProxy.opacity,
    //     0.75,
    //     deltaTime * 3
    //   );
    // } else {
    //   cubeProxy.opacity = MathUtils.lerp(cubeProxy.opacity, 0, deltaTime * 3);
    // }

    if (current.s === "Loading" || current.s === "Viewing") {
      current.arControls.update(elapsedTime, deltaTime);
    }
  };

  let updatedCanvasWidth = 0;
  let updatedCanvasHeight = 0;
  let allowCanvasResize = true;
  const resize = () => {
    if (!player) {
      console.warn(
        "ejPlayerPipelineModule: onCanvasSizeChange but no `player` to update."
      );
      return;
    }
    const canvasWidth = window.innerWidth;
    const canvasHeight = window.innerHeight;
    const devicePixelRatio = 1;
    const updateCameraAndRenderer = true;

    if (allowCanvasResize) {
      updatedCanvasWidth = canvasWidth;
      updatedCanvasHeight = canvasHeight;
    }
    player.resize(
      updatedCanvasWidth,
      updatedCanvasHeight,
      devicePixelRatio,
      updateCameraAndRenderer
    );
    emitter?.emit("resized", {
      width: canvasWidth,
      height: canvasHeight,
      dpi: devicePixelRatio,
    });
    allowCanvasResize = true;
  };
  if (noAR) resize();

  if (fsm.can("StartRepositioning")) {
    fsm.dispatch("StartRepositioning", artworkData[1]);
  } else {
    console.log("FSM cannot StartRepositioning from state", fsm.value);
  }

  async function loadArtwork(artwork: ArtworkData) {
    if (!artwork) {
      throw new Error(`initExperienceRenderer: No artwork found`);
    }
    if (fsm.can("StartRepositioning")) {
      // const contentModule = await createContentModule();
      fsm.dispatch("StartRepositioning", artwork);
    } else {
      console.log("FSM cannot StartRepositioning from state", fsm.value);
    }
    // fix cube disappearing
    player.scene.traverse((object) => {
      object.frustumCulled = false;
    });
  }

  function clearArtwork() {
    if (fsm.can("ClearArtwork")) {
      fsm.dispatch("ClearArtwork");
      // Cleanup
      emitter?.emit("content-load-progress", oldProgress);
    }
  }

  function repositionArtwork() {
    if (fsm.can("RepositionCurrent")) {
      fsm.dispatch("RepositionCurrent");
    }
  }

  function showReticle() {
    if (fsm.value.s === "Positioning") {
      reticle.visible = true;
      if (!hasBeenRepositioned) player.scene.add(cubeProxy.container);
    }
  }

  function hideReticle() {
    if (fsm.value.s === "Positioning") {
      reticle.visible = false;
    }
  }

  return {
    loadArtwork,
    clearArtwork,
    repositionArtwork,
    showReticle,
    hideReticle,
    hasBeenRepositioned,
  };
}
